import { Show, createSignal, type Component } from 'solid-js';
import { MessageSquare, UserRound } from 'lucide-solid';
import { authApi } from '~/shared/api/auth-api';
import { Button } from '~/shared/ui/Button';
import { DialogShell } from '~/shared/ui/DialogShell';
import { TextField } from '~/shared/ui/TextField';
import { maskPhone, refusalMessage } from '~/widgets/shell/smsCodeText';

export const MyProfileDialog: Component<{
  open: boolean;
  user: { firstName?: string | null; lastName?: string | null; phone?: string | null } | null | undefined;
  onClose: () => void;
  onRenamed: (name: { firstName: string | null; lastName: string | null }) => void;
  onSignedOut: () => void;
}> = (props) => {
  const [firstName, setFirstName] = createSignal(props.user?.firstName ?? '');
  const [lastName, setLastName] = createSignal(props.user?.lastName ?? '');
  const [savingName, setSavingName] = createSignal(false);
  const [nameError, setNameError] = createSignal<string | null>(null);

  const [phone, setPhone] = createSignal('');
  const [code, setCode] = createSignal('');
  const [sent, setSent] = createSignal(false);
  const [sending, setSending] = createSignal(false);
  const [confirming, setConfirming] = createSignal(false);
  const [phoneError, setPhoneError] = createSignal<string | null>(null);

  const nameDirty = () =>
    firstName().trim() !== (props.user?.firstName ?? '').trim() ||
    lastName().trim() !== (props.user?.lastName ?? '').trim();

  const saveName = async () => {
    if (!nameDirty() || savingName()) return;
    setNameError(null);
    setSavingName(true);
    try {
      await authApi.updateMyName({ firstName: firstName().trim(), lastName: lastName().trim() });
      props.onRenamed({ firstName: firstName().trim() || null, lastName: lastName().trim() || null });
    } catch (err) {
      setNameError(refusalMessage(err, 'Не удалось сохранить имя'));
    } finally {
      setSavingName(false);
    }
  };

  const sendCode = async () => {
    if (!phone().trim() || sending()) return;
    setPhoneError(null);
    setSending(true);
    try {
      await authApi.sendMyPhoneCode(phone().trim());
      setSent(true);
    } catch (err) {
      setPhoneError(refusalMessage(err, 'Не удалось отправить код'));
    } finally {
      setSending(false);
    }
  };

  const confirm = async () => {
    if (!sent() || !code().trim() || confirming()) return;
    setPhoneError(null);
    setConfirming(true);
    try {
      const changed = await authApi.confirmMyPhone({ phone: phone().trim(), code: code().trim() });
      if (changed.signedOut) props.onSignedOut();
    } catch (err) {
      setPhoneError(refusalMessage(err, 'Не удалось сменить номер'));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Show when={props.open}>
      <DialogShell onClose={props.onClose} maxWidth="440px">
        <div class="flex flex-col gap-5 p-5" role="form" aria-label="Мой профиль">
          <div class="flex items-center gap-2.5">
            <UserRound class="h-4.5 w-4.5 text-[var(--color-text-tertiary)]" aria-hidden="true" />
            <h2 class="text-[15px] font-semibold">Мой профиль</h2>
          </div>

          <section class="flex flex-col gap-3">
            <div class="grid grid-cols-2 gap-3">
              <TextField label="Имя" value={firstName()} onInput={(e) => setFirstName(e.currentTarget.value)} />
              <TextField label="Фамилия" value={lastName()} onInput={(e) => setLastName(e.currentTarget.value)} />
            </div>
            <Show when={nameError()}>
              <p class="text-[12.5px]" style={{ color: 'var(--color-danger)' }} role="alert">
                {nameError()}
              </p>
            </Show>
            <div class="flex justify-end">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={!nameDirty() || savingName()}
                loading={savingName()}
                onClick={() => void saveName()}
              >
                Сохранить имя
              </Button>
            </div>
          </section>

          <section class="flex flex-col gap-3 border-t border-hairline pt-4">
            <p class="text-[12.5px] leading-snug text-[var(--color-text-tertiary)]">
              <Show when={props.user?.phone} fallback={'Телефона нет — на него приходит код входа сотрудника.'}>
                {(current) => <>Сейчас: {maskPhone(current())}. </>}
              </Show>{' '}
              Новый номер подтвердим кодом из SMS на него. После смены номера войти придётся заново.
            </p>
            <div class="flex items-end gap-2">
              <div class="flex-1">
                <TextField
                  label="Новый телефон"
                  type="tel"
                  inputmode="tel"
                  autocomplete="tel"
                  value={phone()}
                  onInput={(e) => {
                    setPhone(e.currentTarget.value);
                    setSent(false);
                  }}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={sending()}
                disabled={!phone().trim() || sending()}
                onClick={() => void sendCode()}
              >
                {sent() ? 'Выслать ещё раз' : 'Выслать код'}
              </Button>
            </div>
            <Show when={sent()}>
              <p class="flex items-center gap-1.5 text-[12px] text-[var(--color-text-tertiary)]">
                <MessageSquare class="h-3.5 w-3.5" aria-hidden="true" />
                Код отправлен на {maskPhone(phone())}
              </p>
              <TextField
                label="Код из SMS"
                digits
                autocomplete="one-time-code"
                value={code()}
                onInput={(e) => setCode(e.currentTarget.value)}
              />
            </Show>
            <Show when={phoneError()}>
              <p class="text-[12.5px]" style={{ color: 'var(--color-danger)' }} role="alert">
                {phoneError()}
              </p>
            </Show>
            <div class="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={props.onClose}>
                Закрыть
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={!sent() || !code().trim() || confirming()}
                loading={confirming()}
                onClick={() => void confirm()}
              >
                Сменить номер
              </Button>
            </div>
          </section>
        </div>
      </DialogShell>
    </Show>
  );
};
